import { isIncomingSwish } from '@budgie/shared';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { SearchIcon, SlidersHorizontalIcon, XIcon } from 'lucide-react';
import {
  type ComponentProps,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { CreateBillDialog } from '@/components/purchases/CreateBillDialog';
import { dayLabel } from '@/components/purchases/dates';
import {
  type FilterKey,
  FilterSheet,
  type FilterValues,
  HIDDEN_LABELS,
  YES_NO_LABELS,
} from '@/components/purchases/FilterSheet';
import { InboxStrip } from '@/components/purchases/InboxStrip';
import { PurchaseDetailSheet } from '@/components/purchases/PurchaseDetailSheet';
import { type PurchaseItem, PurchaseRow } from '@/components/purchases/PurchaseRow';
import { SavingsPickerSheet } from '@/components/savings/SavingsPickerSheet';
import { CategoryPickerSheet } from '@/components/transactions/CategoryPickerSheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { invalidateFinancials } from '../../lib/invalidate';
import { trpc } from '../../lib/trpc';
import { usePageTitle } from '../../lib/usePageTitle';

const PAGE_SIZE = 50;

const searchSchema = z.object({
  q: z.string().optional().catch(undefined),
  categoryId: z.string().uuid().optional().catch(undefined),
  accountId: z.string().uuid().optional().catch(undefined),
  isTransfer: z.enum(['true', 'false']).optional().catch(undefined),
  isExcluded: z.enum(['true', 'false']).optional().catch(undefined),
  /** Unset: hidden rows are left out. 'all' shows them too, 'true' only them. */
  isHidden: z.enum(['all', 'true']).optional().catch(undefined),
  needsReview: z.enum(['true', 'false']).optional().catch(undefined),
  sortBy: z.enum(['date', 'amount', 'description']).optional().catch(undefined),
  sortDir: z.enum(['asc', 'desc']).optional().catch(undefined),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
});

export const Route = createFileRoute('/transactions/')({
  validateSearch: searchSchema,
  component: PurchasesPage,
});

const SORT_OPTIONS = [
  { value: 'date:desc', label: 'Nyast först' },
  { value: 'date:asc', label: 'Äldst först' },
  { value: 'amount:asc', label: 'Största utgift först' },
  { value: 'amount:desc', label: 'Största inkomst först' },
  { value: 'description:asc', label: 'Beskrivning A–Ö' },
  { value: 'description:desc', label: 'Beskrivning Ö–A' },
];

const FILTER_KEYS: FilterKey[] = [
  'categoryId',
  'accountId',
  'isTransfer',
  'isExcluded',
  'isHidden',
  'needsReview',
];

const toBool = (v: 'true' | 'false' | undefined) =>
  v === 'true' ? true : v === 'false' ? false : undefined;

/** Splits a date-sorted page into day groups, keeping order. */
function groupByDate(items: PurchaseItem[]) {
  const groups: { date: string; items: PurchaseItem[] }[] = [];
  for (const item of items) {
    const last = groups[groups.length - 1];
    if (last && last.date === item.date) last.items.push(item);
    else groups.push({ date: item.date, items: [item] });
  }
  return groups;
}

function PurchasesPage() {
  usePageTitle('Köp');

  const search = useSearch({ from: '/transactions/' });
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const [queryInput, setQueryInput] = useState(search.q ?? '');
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Rows stay set while their sheet animates closed so content doesn't flash.
  const [pickerRow, setPickerRow] = useState<PurchaseItem | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<PurchaseItem | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [billFor, setBillFor] = useState<PurchaseItem | null>(null);
  const [savingsRow, setSavingsRow] = useState<PurchaseItem | null>(null);
  const [savingsOpen, setSavingsOpen] = useState(false);

  const page = search.page ?? 1;
  const sortBy = search.sortBy ?? 'date';
  const sortDir = search.sortDir ?? 'desc';

  const txnQuery = trpc.transaction.search.useQuery(
    {
      query: search.q || undefined,
      categoryId: search.categoryId,
      accountId: search.accountId,
      isTransfer: toBool(search.isTransfer),
      isExcluded: toBool(search.isExcluded),
      isHidden: search.isHidden === 'true' ? true : search.isHidden === 'all' ? undefined : false,
      needsReview: toBool(search.needsReview),
      sortBy,
      sortDir,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    },
    { placeholderData: (prev) => prev },
  );
  const categoriesQuery = trpc.category.listGroups.useQuery();
  const accountsQuery = trpc.bankAccount.list.useQuery();

  const updateSearch = useCallback(
    (updates: Record<string, string | undefined>) => {
      void navigate({
        from: '/transactions/',
        search: (prev) => ({
          ...prev,
          ...updates,
          page: updates.page ? Number(updates.page) : 1,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  // Search as you type, without a navigation per keystroke.
  useEffect(() => {
    const next = queryInput.trim();
    if (next === (search.q ?? '')) return;
    const timer = window.setTimeout(() => updateSearch({ q: next || undefined }), 300);
    return () => window.clearTimeout(timer);
  }, [queryInput, search.q, updateSearch]);

  const clearAll = () => {
    setQueryInput('');
    void navigate({ from: '/transactions/', search: {}, replace: true });
  };

  // --- Mutations -----------------------------------------------------------

  const createRule = trpc.transaction.categorize.useMutation({
    onSuccess: async () => {
      await Promise.all([invalidateFinancials(utils), utils.categoryRule.invalidate()]);
      toast.success('Regel skapad');
    },
    onError: (error) => toast.error(error.message),
  });

  const categorize = trpc.transaction.categorize.useMutation({
    onSuccess: async (_data, variables) => {
      await Promise.all([
        invalidateFinancials(utils),
        variables.createRule ? utils.categoryRule.invalidate() : undefined,
      ]);
      if (variables.createRule) {
        toast.success('Kategori ändrad och regel skapad');
        return;
      }
      // Only the category picker categorizes here, so pickerRow is the row
      const row = pickerRow?.id === variables.transactionId ? pickerRow : null;
      if (row && isIncomingSwish(row.description, row.amount)) {
        // Incoming Swish is sorted by hand, never by a rule
        toast.success('Kategori ändrad');
        return;
      }
      toast.success('Kategori ändrad', {
        action: {
          label: 'Skapa regel',
          onClick: () => createRule.mutate({ ...variables, createRule: true }),
        },
      });
    },
    onError: (error) => toast.error(error.message),
  });

  // Marking savings is done by hand each time: no rule, no "Skapa regel"
  // offer. Same path as the inbox, so it also clears "Granska".
  const markSavings = trpc.sync.resolve.useMutation({
    onSuccess: async () => {
      await invalidateFinancials(utils);
    },
    onError: (error) => toast.error(error.message),
  });

  const setExcluded = trpc.transaction.setExcluded.useMutation({
    onSuccess: async (_data, { transactionId, isExcluded }) => {
      setDetailRow((r) => (r?.id === transactionId ? { ...r, isExcluded } : r));
      await invalidateFinancials(utils);
      toast.success(isExcluded ? 'Räknas inte med i budgeten' : 'Räknas med i budgeten igen');
    },
    onError: (error) => toast.error(error.message),
  });

  const setHidden = trpc.transaction.setHidden.useMutation({
    onSuccess: async (_data, { transactionId, isHidden }) => {
      setDetailRow((r) => (r?.id === transactionId ? { ...r, isHidden } : r));
      // A hidden row leaves the list, so close its sheet with it
      if (isHidden && search.isHidden === undefined) setDetailOpen(false);
      await invalidateFinancials(utils);
      toast.success(isHidden ? 'Dold i listor, räknas ändå med' : 'Visas i listor igen', {
        action: {
          label: 'Ångra',
          onClick: () => setHidden.mutate({ transactionId, isHidden: !isHidden }),
        },
      });
    },
    onError: (error) => toast.error(error.message),
  });

  const setTransfer = trpc.transaction.setTransferFlag.useMutation({
    onSuccess: async (_data, { transactionId, isTransfer }) => {
      setDetailRow((r) => (r?.id === transactionId ? { ...r, isTransfer } : r));
      await invalidateFinancials(utils);
      toast.success(
        isTransfer ? 'Markerad som överföring mellan egna konton' : 'Räknas som köp igen',
      );
    },
    onError: (error) => toast.error(error.message),
  });

  // --- Derived ---------------------------------------------------------------

  const categoryGroups = useMemo(
    () =>
      (categoriesQuery.data ?? []).map((g) => ({
        label: g.name,
        options: g.categories.map((c) => ({ value: c.id, label: c.name })),
      })),
    [categoriesQuery.data],
  );
  const accounts = useMemo(
    () =>
      (accountsQuery.data ?? []).map((a) => ({
        value: a.id,
        label: a.friendlyName ?? a.accountNumber ?? a.id,
      })),
    [accountsQuery.data],
  );

  const filterValues: FilterValues = {
    categoryId: search.categoryId,
    accountId: search.accountId,
    isTransfer: search.isTransfer,
    isExcluded: search.isExcluded,
    isHidden: search.isHidden,
    needsReview: search.needsReview,
  };
  const activeKeys = FILTER_KEYS.filter((k) => filterValues[k]);
  const hasAnyFilter = Boolean(search.q) || activeKeys.length > 0;

  const chipLabel = (key: FilterKey): string => {
    const value = filterValues[key];
    if (!value) return '';
    if (key === 'categoryId') {
      for (const g of categoryGroups) {
        const hit = g.options.find((o) => o.value === value);
        if (hit) return hit.label;
      }
      return 'Kategori';
    }
    if (key === 'accountId') return accounts.find((a) => a.value === value)?.label ?? 'Konto';
    if (key === 'isHidden') return HIDDEN_LABELS[value as 'all' | 'true'];
    return YES_NO_LABELS[key][value as 'true' | 'false'];
  };

  const data = txnQuery.data;
  const items = data?.items ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const grouped = sortBy === 'date';
  // The detail sheet follows the live row so switches reflect refetched data.
  const liveDetail = (detailRow && items.find((i) => i.id === detailRow.id)) ?? detailRow;

  const openPicker = (row: PurchaseItem) => {
    setPickerRow(row);
    setPickerOpen(true);
  };
  const openDetail = (row: PurchaseItem) => {
    setDetailRow(row);
    setDetailOpen(true);
  };

  const renderRow = (row: PurchaseItem, showDate: boolean) => (
    <PurchaseRow
      key={row.id}
      row={row}
      showDate={showDate}
      onOpen={() => openDetail(row)}
      onPickCategory={() => openPicker(row)}
    />
  );

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      {/* Title + search */}
      <header className="space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-semibold tracking-tight">Köp</h1>
          <search className="sm:w-72">
            <form
              className="relative"
              onSubmit={(e) => {
                e.preventDefault();
                updateSearch({ q: queryInput.trim() || undefined });
              }}
            >
              <SearchIcon
                aria-hidden
                className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                type="search"
                inputMode="search"
                aria-label="Sök bland köp"
                placeholder="Sök köp…"
                className="h-11 rounded-xl bg-card pl-9 sm:h-9"
                value={queryInput}
                onChange={(e) => setQueryInput(e.target.value)}
              />
            </form>
          </search>
        </div>

        {/* Filter chips */}
        <div className="-mx-4 flex scrollbar-none items-center gap-2 overflow-x-auto px-4 sm:mx-0 sm:flex-wrap sm:px-0">
          <Chip
            onClick={() => setFiltersOpen(true)}
            active={activeKeys.length > 0}
            aria-haspopup="dialog"
          >
            <SlidersHorizontalIcon aria-hidden className="size-3.5" />
            Filter
            {activeKeys.length > 0 && (
              <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground tabular-nums">
                <span className="sr-only">, aktiva: </span>
                {activeKeys.length}
              </span>
            )}
          </Chip>

          {activeKeys.map((key) => (
            <Chip
              key={key}
              active
              onClick={() => updateSearch({ [key]: undefined })}
              aria-label={`Ta bort filter: ${chipLabel(key)}`}
            >
              <span className="max-w-40 truncate">{chipLabel(key)}</span>
              <XIcon aria-hidden className="size-3.5 opacity-70" />
            </Chip>
          ))}

          {!search.needsReview && (
            <Chip onClick={() => updateSearch({ needsReview: 'true' })}>Behöver granskas</Chip>
          )}
          {!search.isTransfer && (
            <Chip onClick={() => updateSearch({ isTransfer: 'false' })}>Utan överföringar</Chip>
          )}

          {hasAnyFilter && (
            <button
              type="button"
              onClick={clearAll}
              className="shrink-0 px-2 text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              Rensa
            </button>
          )}
        </div>
      </header>

      {/* Sorting chores first, but only on the plain unfiltered view */}
      {!hasAnyFilter && page === 1 && <InboxStrip />}

      {/* Results */}
      <section aria-label="Köp" className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground tabular-nums" aria-live="polite">
            {data ? `${data.total} köp` : ' '}
          </p>
          <Select
            value={`${sortBy}:${sortDir}`}
            onValueChange={(v) => {
              const [by, dir] = v.split(':');
              updateSearch({ sortBy: by, sortDir: dir, page: '1' });
            }}
          >
            <SelectTrigger
              size="sm"
              aria-label="Sortering"
              className="border-transparent bg-transparent text-sm text-muted-foreground hover:text-foreground dark:bg-transparent"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="end" position="popper">
              {SORT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {txnQuery.isLoading ? (
          <ListSkeleton />
        ) : txnQuery.isError ? (
          <p className="rounded-2xl border border-border bg-card p-6 text-center text-sm text-negative">
            Kunde inte ladda köpen.
          </p>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-border bg-card p-8 text-center">
            <p className="text-[15px] font-semibold">
              {hasAnyFilter ? 'Inga köp matchar' : 'Inga köp än'}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {hasAnyFilter
                ? 'Prova en annan sökning eller ta bort något filter.'
                : 'Köp dyker upp här när banken synkas.'}
            </p>
            {hasAnyFilter && (
              <Button type="button" variant="outline" className="mt-4" onClick={clearAll}>
                Rensa filter
              </Button>
            )}
          </div>
        ) : (
          <div
            className={cn(
              'space-y-4 transition-opacity',
              txnQuery.isPlaceholderData && 'opacity-60',
            )}
          >
            {grouped ? (
              groupByDate(items).map((group) => (
                <div key={group.date}>
                  <h2 className="px-1 pb-1.5 text-xs font-medium text-muted-foreground">
                    {dayLabel(group.date)}
                  </h2>
                  <ul className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card">
                    {group.items.map((row) => renderRow(row, false))}
                  </ul>
                </div>
              ))
            ) : (
              <ul className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card">
                {items.map((row) => renderRow(row, true))}
              </ul>
            )}
          </div>
        )}

        {data && totalPages > 1 && (
          <nav aria-label="Sidnavigering" className="flex items-center justify-between gap-2 pt-1">
            <Button
              variant="outline"
              disabled={page <= 1}
              onClick={() => updateSearch({ page: String(page - 1) })}
            >
              ← Föregående
            </Button>
            <span className="text-sm text-muted-foreground tabular-nums">
              Sida {page} av {totalPages}
            </span>
            <Button
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => updateSearch({ page: String(page + 1) })}
            >
              Nästa →
            </Button>
          </nav>
        )}
      </section>

      <FilterSheet
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        values={filterValues}
        onChange={(key, value) => updateSearch({ [key]: value })}
        onClear={clearAll}
        categoryGroups={categoryGroups}
        accounts={accounts}
        total={data?.total}
      />

      <PurchaseDetailSheet
        row={liveDetail}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        pending={
          setExcluded.isPending ||
          setHidden.isPending ||
          setTransfer.isPending ||
          markSavings.isPending
        }
        onPickCategory={() => {
          if (!liveDetail) return;
          setDetailOpen(false);
          openPicker(liveDetail);
        }}
        onMarkSavings={() => {
          if (!liveDetail) return;
          setDetailOpen(false);
          setSavingsRow(liveDetail);
          setSavingsOpen(true);
        }}
        onSetTransfer={(isTransfer) => {
          if (liveDetail) setTransfer.mutate({ transactionId: liveDetail.id, isTransfer });
        }}
        onSetExcluded={(isExcluded) => {
          if (liveDetail) setExcluded.mutate({ transactionId: liveDetail.id, isExcluded });
        }}
        onSetHidden={(isHidden) => {
          if (liveDetail) setHidden.mutate({ transactionId: liveDetail.id, isHidden });
        }}
        onCreateBill={() => {
          if (!liveDetail) return;
          setDetailOpen(false);
          setBillFor(liveDetail);
        }}
      />

      <CreateBillDialog transaction={billFor} onClose={() => setBillFor(null)} />

      <SavingsPickerSheet
        open={savingsOpen}
        onOpenChange={setSavingsOpen}
        title={savingsRow?.description}
        value={savingsRow?.categoryId}
        onSelect={(pot) => {
          if (!savingsRow) return;
          // Already saved there (and not a transfer or up for review): nothing to do
          if (
            pot.id === savingsRow.categoryId &&
            !savingsRow.isTransfer &&
            !savingsRow.needsReview
          ) {
            toast(`Redan sparat i ${pot.name}`);
            return;
          }
          markSavings.mutate(
            { transactionId: savingsRow.id, categoryId: pot.id, createRule: false },
            { onSuccess: () => toast.success(`Sparat i ${pot.name}`) },
          );
        }}
      />

      <CategoryPickerSheet
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        value={pickerRow?.categoryId ?? null}
        title={pickerRow ? `Kategori för ${pickerRow.description}` : 'Välj kategori'}
        showCreateRule={!pickerRow || !isIncomingSwish(pickerRow.description, pickerRow.amount)}
        onSelect={(categoryId, { createRule: withRule }) => {
          if (!pickerRow) return;
          // Re-picking the current category is a no-op unless a rule was requested.
          if (categoryId === pickerRow.categoryId && !withRule) return;
          categorize.mutate({
            transactionId: pickerRow.id,
            categoryId,
            createRule: withRule,
          });
        }}
      />
    </div>
  );
}

/** Compact pill used for filters; `active` marks an applied filter. */
function Chip({
  active = false,
  children,
  className,
  ...props
}: ComponentProps<'button'> & { active?: boolean; children: ReactNode }) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border px-3 text-sm font-medium whitespace-nowrap outline-none transition-colors focus-visible:ring-3 focus-visible:ring-ring/50 max-sm:h-10',
        active
          ? 'border-primary/25 bg-primary/10 text-primary hover:bg-primary/15'
          : 'border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

function ListSkeleton() {
  return (
    <div className="space-y-4">
      {['a', 'b'].map((k) => (
        <div key={k}>
          <Skeleton className="mb-2 ml-1 h-3 w-16" />
          <div className="divide-y divide-border rounded-2xl border border-border bg-card">
            {['1', '2', '3'].map((r) => (
              <div key={r} className="flex items-start justify-between gap-3 px-4 py-3">
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-6 w-24 rounded-full" />
                </div>
                <Skeleton className="h-4 w-16" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
